// BID OFFICE TEMPLATES in the web (AB#444): a bid's library of PowerPoint and Word templates
// (backend/domain/office-templates.ts, routes/office-templates.ts) as cached server state, and the
// words for every refusal. Not the ARTEFACT template library (lib/template-library.ts).
import type { QueryClient } from '@tanstack/react-query'
import type { Key, Lang } from '../i18n'
import { invalidateSlidesTemplates } from '../slides/template-query'
import { api, isApiError, type DraftAuthor, type OfficeTemplate, type OfficeTemplateKind, type TemplateSize } from './api'
import { downloadBinary } from './download'
import { formatMegabytes, templateSizeFromError, templateSizeOf } from './template-size'

/** Templates a bid may hold per kind, an imported one counted like any other (backend MAX_LIBRARY_TEMPLATES). */
export const MAX_LIBRARY_TEMPLATES = 10

/** Every library query of a bid sits under `['office-templates', bid]`: one invalidation reaches all. */
export const officeTemplatesKey = (bid: string, kind?: OfficeTemplateKind) =>
  kind ? (['office-templates', bid, kind] as const) : (['office-templates', bid] as const)
export const officeTemplateVersionsKey = (bid: string, id: string) => ['office-templates', bid, 'versions', id] as const

/** A bid's templates of one kind — the Bid settings lists and the artefact pickers share it. */
export const officeTemplatesQuery = (bid: string, kind: OfficeTemplateKind) => ({
  queryKey: officeTemplatesKey(bid, kind),
  queryFn: async (): Promise<OfficeTemplate[]> => (await api.officeTemplates(bid, kind)).templates,
})

/** The templates of a kind counted against the limit: every one, imported or not. */
export const countedTemplates = (templates: readonly OfficeTemplate[]): number => templates.length

/**
 * Is a slide size 16:9 — the only ratio a PowerPoint library takes (backend
 * domain/office-templates.ts#isSixteenByNine, same 0.5 % tolerance). A corporate catalogue entry
 * that is not is never offered.
 */
export function isSixteenByNine(size: { cx: number; cy: number }): boolean {
  if (!(size.cx > 0 && size.cy > 0)) return false
  return Math.abs(size.cx / size.cy - 16 / 9) <= (16 / 9) * 0.005
}

/** How a template's origin reads in a list row. */
export const ORIGIN_KEY = {
  catalogue: 'otpl_origin_corporate',
  upload: 'otpl_origin_uploaded',
  imported: 'otpl_origin_imported',
  legacy: 'otpl_origin_existing',
} as const satisfies Record<OfficeTemplate['origin']['type'], Key>

/** A refused FILE, worded after "This template was not saved:" (as the uploads elsewhere). */
const FILE_REFUSAL_KEY: Record<OfficeTemplateKind, Record<string, Key>> = {
  slides: {
    not_pptx: 'brand_tpl_not_pptx',
    macro_enabled: 'brand_tpl_pptx_macro',
    not_16_9: 'otpl_err_not_16_9',
    too_large: 'brand_tpl_too_large',
  },
  word: {
    not_word: 'brand_tpl_not_word',
    macro_enabled: 'brand_tpl_macro_enabled',
    no_content_marker: 'brand_tpl_no_content_marker',
    too_large: 'brand_tpl_too_large',
  },
}

/** A library rule refusing a write (routes/office-templates.ts), as a sentence of its own. */
const RULE_REFUSAL_KEY: Record<string, Key> = {
  library_full: 'otpl_err_library_full',
  duplicate: 'otpl_err_duplicate',
  name_taken: 'otpl_err_name_taken',
  template_changed: 'otpl_err_template_changed',
  is_default: 'otpl_why_default',
  in_use: 'otpl_err_in_use',
  bad_name: 'corp_tpl_err_bad_name',
  bad_master: 'corp_tpl_err_bad_master',
  bad_replacement: 'otpl_err_bad_replacement',
  unknown_template: 'otpl_err_unknown',
  not_found: 'otpl_err_not_found',
  no_version: 'otpl_err_not_found',
  wrong_kind: 'otpl_err_wrong_kind',
  wrong_type: 'otpl_err_wrong_kind',
  not_slides: 'otpl_err_wrong_kind',
  forbidden: 'otpl_err_forbidden',
  // The shared draft (AB#453/454).
  draft_pending: 'otpl_err_draft_pending',
  template_unreadable: 'otpl_err_unreadable',
  missing_image: 'otpl_err_missing_image',
  unknown_field: 'otpl_err_bad_setting',
  bad_value: 'otpl_err_bad_setting',
}

/** A size refusal in plain words, per the limit the file broke. */
const SIZE_KEY = {
  upload: 'tpl_too_large',
  kept: 'tpl_too_large_kept',
  expanded: 'tpl_too_large_expanded',
} as const satisfies Record<TemplateSize['stage'], Key>

export function sizeText(size: TemplateSize, t: (k: Key) => string, lang: Lang): string {
  return t(SIZE_KEY[size.stage])
    .replace('{size}', () => formatMegabytes(size.bytes, lang))
    .replace('{limit}', () => formatMegabytes(size.limit, lang))
}

/** The files a library of each kind takes, as a file input's `accept`. */
export const TEMPLATE_FILE_ACCEPT: Record<OfficeTemplateKind, string> = { slides: '.pptx,.potx', word: '.docx,.dotx' }

/**
 * Why a file may not be sent as a template of `kind`, or null when it may: a drop and a pick are
 * vetted alike, before anything is sent. A drop bypasses the input's `accept`, so the extension is
 * checked here, the refusal worded as the server's `not_pptx` / `not_word`.
 */
export function templateFileRefusal(file: Pick<File, 'name' | 'size'>, kind: OfficeTemplateKind, t: (k: Key) => string, lang: Lang): string | null {
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
  if (!TEMPLATE_FILE_ACCEPT[kind].split(',').includes(extension)) {
    return `${t('brand_tpl_refused')} ${t(kind === 'slides' ? 'brand_tpl_not_pptx' : 'brand_tpl_not_word')}`
  }
  const tooLarge = templateSizeOf(kind, file.size)
  return tooLarge ? sizeText(tooLarge, t, lang) : null
}

/** Why a write to a library of `kind` failed, in words. */
export function officeRefusalText(e: unknown, kind: OfficeTemplateKind, t: (k: Key) => string, lang: Lang): string {
  const size = templateSizeFromError(e)
  if (size) return sizeText(size, t, lang)
  if (!isApiError(e)) return t('corp_tpl_err_generic')
  const fileKey = e.status === 413 ? 'brand_tpl_too_large' : FILE_REFUSAL_KEY[kind][e.code]
  if (fileKey) return `${t('brand_tpl_refused')} ${t(fileKey)}`
  const key = RULE_REFUSAL_KEY[e.code]
  if (!key) return t('corp_tpl_err_generic')
  if (e.code === 'library_full') return t(key).replace('{max}', () => String(MAX_LIBRARY_TEMPLATES))
  // 409 draft_pending names who holds the unpublished changes.
  const by = e.code === 'draft_pending' ? draftPendingBy(e) : []
  return by.length ? `${t(key)} (${authorsText(by, t)})` : t(key)
}

/** Who holds a draft's unpublished changes, as a 409 `draft_pending { by }` names them. */
export function draftPendingBy(e: unknown): DraftAuthor[] {
  if (!isApiError(e) || e.code !== 'draft_pending') return []
  const by = e.details?.by
  return Array.isArray(by) ? (by as DraftAuthor[]) : []
}

/** One author of draft changes in words: "Alice", or — an agent — "Alice's agent". */
export const authorText = (a: Pick<DraftAuthor, 'name' | 'kind'>, t: (k: Key) => string): string =>
  a.kind === 'agent' ? t('sec_lock_agent').replace('{name}', () => a.name) : a.name

/** Authors of draft changes, in words: "Alice, Bob's agent". */
export const authorsText = (by: readonly Pick<DraftAuthor, 'name' | 'kind'>[], t: (k: Key) => string): string =>
  by.map((a) => authorText(a, t)).join(', ')

/** A template's shared draft (the template view's first read, AB#454). */
export const officeTemplateDraftKey = (bid: string, id: string) => ['office-templates', bid, 'draft', id] as const

/** Does a listed template's draft hold unpublished settings (what a publish would write)? */
export const hasUnpublished = (t: Pick<OfficeTemplate, 'draft'>): boolean => (t.draft?.fields.length ?? 0) > 0

/** A stale `baseVersion` (409 `template_changed`): the member reloads, then tries again. */
export const isTemplateChanged = (e: unknown): boolean => isApiError(e) && e.code === 'template_changed'

/** The artefacts a 409 `in_use` names, or null for any other failure. */
export function inUseBy(e: unknown): Array<{ artefactId: string; title: string }> | null {
  if (!isApiError(e) || e.code !== 'in_use') return null
  const usedBy = e.details?.usedBy
  return Array.isArray(usedBy) ? (usedBy as Array<{ artefactId: string; title: string }>) : []
}

/**
 * Refetch what a write to a bid's Office templates can change: its lists (and versions), its
 * legacy template-set view (the brand derives from the default PowerPoint template), its artefacts'
 * own templates (a delete with a replacement moves them) — and its decks' templates (every deck
 * directory, whose default the artefact picker changes, and every template view, AB#445), so an
 * open slides editor redraws.
 */
export async function refreshOfficeTemplates(client: QueryClient, bid: string): Promise<void> {
  await Promise.all([
    client.invalidateQueries({ queryKey: officeTemplatesKey(bid) }),
    client.invalidateQueries({ queryKey: ['template-sets', 'bid', bid] }),
    client.invalidateQueries({ queryKey: ['docs', bid] }),
    invalidateSlidesTemplates(client, bid),
  ])
}

/**
 * After a publish (AB#454, the stateless notice): the bid's template lists (their versions, drafts
 * and counts) and its decks' templates — a new version is a new view key, so open decks redraw.
 */
export async function refreshAfterPublish(client: QueryClient, bid: string): Promise<void> {
  await Promise.all([client.invalidateQueries({ queryKey: officeTemplatesKey(bid) }), invalidateSlidesTemplates(client, bid)])
}

/** Save a template's file as stored (.pptx / .potx / .docx / .dotx — the server names it). */
export function downloadOfficeTemplate(bid: string, template: Pick<OfficeTemplate, 'id' | 'kind' | 'name'>): Promise<boolean> {
  const format = template.kind === 'slides' ? 'pptx' : 'docx'
  return downloadBinary(
    `/v1/bids/${encodeURIComponent(bid)}/office-templates/${encodeURIComponent(template.id)}/download`,
    { fallbackName: `${template.name}.${format}`, format },
  )
}
